<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceForm,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;

const products = ref([]);
const loading = ref(true);
const error = ref('');

const search = ref('');
const statusFilter = ref('all');
const sortKey = ref('updated');
const sortDir = ref('desc');

const page = ref(1);
const pageSize = 50;

const counts = computed(() => ({
  all: products.value.length,
  active: products.value.filter(p => p.status === 'active').length,
  draft: products.value.filter(p => p.status !== 'active').length,
}));

const processed = computed(() => {
  const q = search.value.trim().toLowerCase();
  let list = products.value.filter(p => {
    if (statusFilter.value === 'active' && p.status !== 'active') return false;
    if (statusFilter.value === 'draft' && p.status === 'active') return false;
    if (q && !p.title?.toLowerCase().includes(q)) return false;
    return true;
  });
  const dir = sortDir.value === 'asc' ? 1 : -1;
  list = [...list].sort((a, b) => {
    let av;
    let bv;
    if (sortKey.value === 'title') {
      av = (a.title || '').toLowerCase();
      bv = (b.title || '').toLowerCase();
    } else if (sortKey.value === 'stock') {
      av = a.totalStock || 0;
      bv = b.totalStock || 0;
    } else if (sortKey.value === 'price') {
      av = a.price || 0;
      bv = b.price || 0;
    } else {
      av = a.updatedAt || '';
      bv = b.updatedAt || '';
    }
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
  return list;
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(processed.value.length / pageSize))
);
const paged = computed(() =>
  processed.value.slice((page.value - 1) * pageSize, page.value * pageSize)
);
const rangeLabel = computed(() => {
  if (!processed.value.length) return '0';
  const start = (page.value - 1) * pageSize + 1;
  const end = Math.min(page.value * pageSize, processed.value.length);
  return `${start}–${end} / ${processed.value.length}`;
});

watch([search, statusFilter, sortKey, sortDir], () => {
  page.value = 1;
});

function toggleSort(key) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = key === 'title' ? 'asc' : 'desc';
  }
}
function sortIcon(key) {
  if (sortKey.value !== key) return 'i-lucide-chevrons-up-down';
  return sortDir.value === 'asc'
    ? 'i-lucide-chevron-up'
    : 'i-lucide-chevron-down';
}

function priceLabel(p) {
  if (!p.price) return '—';
  if (p.currency === 'USD') return `$${p.price.toFixed(2)}`;
  if (p.currency === 'EUR') return `€${p.price.toFixed(2)}`;
  return `${p.price.toFixed(2)} ₺`;
}

function newProduct() {
  router.push({ name: 'product_new', params: { accountId } });
}

// 📥 Nebim Excel'i → taslak ürün → doğrudan düzenleme sayfası (Ürün Yükleme Hattı).
const importing = ref(false);
async function importExcel(e) {
  const file = e.target.files && e.target.files[0];
  e.target.value = '';
  if (!file) return;
  importing.value = true;
  error.value = '';
  try {
    const fd = new FormData();
    fd.append('file', file);
    const data = await postMarketplaceForm(
      accountId,
      'product_center/import_excel',
      fd
    );
    const pid = data.product && data.product.id;
    if (data.barcodeMissing > 0) {
      // eslint-disable-next-line no-alert
      window.alert(
        `⚠️ Dikkat: ${data.barcodeMissing} varyantın barkodu Excel'de boş. ` +
          "Ürün açıldı ama barkodları tamamlamadan Ticimax'e göndermeyin."
      );
    }
    router.push({ name: 'product_edit', params: { accountId, id: pid } });
  } catch (err) {
    error.value = 'İçe aktarılamadı: ' + (err.message || 'bilinmeyen hata');
  } finally {
    importing.value = false;
  }
}
function editProduct(p) {
  router.push({ name: 'product_edit', params: { accountId, id: p.id } });
}

async function loadData() {
  loading.value = true;
  error.value = '';
  try {
    const data = await fetchMarketplaceJson(accountId, 'product_center');
    products.value = data.products || [];
  } catch (e) {
    error.value = 'Ürünler yüklenemedi: ' + e.message;
  } finally {
    loading.value = false;
  }
}

onMounted(loadData);
</script>

<template>
  <div
    class="flex flex-col h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <h1 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
        Ürünler
        <span class="text-slate-400 font-normal">{{ counts.all }}</span>
      </h1>
      <div class="flex items-center gap-2">
        <label
          class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 cursor-pointer"
          :class="importing ? 'opacity-60 pointer-events-none' : ''"
        >
          <span
            :class="
              importing
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-file-spreadsheet'
            "
            class="size-4"
          />
          {{ importing ? 'İçe aktarılıyor…' : "📥 Excel'den İçe Aktar" }}
          <input
            type="file"
            accept=".xlsx"
            class="hidden"
            @change="importExcel"
          />
        </label>
        <button
          class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600"
          @click="newProduct"
        >
          <span class="i-lucide-plus size-4" />
          Yeni Ürün
        </button>
      </div>
    </div>

    <!-- Toolbar -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <div
        class="flex items-center gap-1 p-0.5 rounded-lg bg-slate-100 dark:bg-slate-800"
      >
        <button
          v-for="tab in [
            { key: 'all', label: 'Tümü', n: counts.all },
            { key: 'active', label: 'Aktif', n: counts.active },
            { key: 'draft', label: 'Taslak', n: counts.draft },
          ]"
          :key="tab.key"
          class="px-3 py-1.5 text-xs font-medium rounded-md transition"
          :class="
            statusFilter === tab.key
              ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
          "
          @click="statusFilter = tab.key"
        >
          {{ tab.label }}
          <span class="text-slate-400">{{ tab.n }}</span>
        </button>
      </div>

      <div class="flex items-center gap-2">
        <div
          class="flex items-center gap-2 w-56 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-woot-500"
        >
          <span class="i-lucide-search size-4 shrink-0 text-slate-400" />
          <input
            v-model="search"
            type="text"
            placeholder="Ürün ara…"
            class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-0"
          />
        </div>
        <select
          v-model="sortKey"
          class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
        >
          <option value="updated">Son güncelleme</option>
          <option value="title">İsim</option>
          <option value="stock">Stok</option>
          <option value="price">Fiyat</option>
        </select>
        <button
          class="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          :title="sortDir === 'asc' ? 'Artan' : 'Azalan'"
          @click="sortDir = sortDir === 'asc' ? 'desc' : 'asc'"
        >
          <span
            :class="
              sortDir === 'asc'
                ? 'i-lucide-arrow-up-narrow-wide'
                : 'i-lucide-arrow-down-wide-narrow'
            "
            class="size-4"
          />
        </button>
      </div>
    </div>

    <!-- Tablo -->
    <div class="flex-1 overflow-auto">
      <div
        v-if="loading"
        class="flex items-center justify-center h-48 text-slate-400 gap-2"
      >
        <span class="i-lucide-loader-2 size-5 animate-spin" />
        <span class="text-sm">Yükleniyor…</span>
      </div>

      <div
        v-else-if="error"
        class="m-6 flex items-center gap-3 p-4 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm"
      >
        <span class="i-lucide-alert-circle size-5 shrink-0" />{{ error }}
      </div>

      <div
        v-else-if="processed.length === 0"
        class="flex flex-col items-center justify-center h-64 text-slate-400 gap-3"
      >
        <span class="i-lucide-package-search size-12" />
        <span class="text-sm">Ürün bulunamadı.</span>
        <button
          class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600"
          @click="newProduct"
        >
          <span class="i-lucide-plus size-4" /> Yeni Ürün
        </button>
      </div>

      <table v-else class="w-full text-sm border-collapse">
        <thead
          class="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/95 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700"
        >
          <tr>
            <th class="px-6 py-2.5 text-left font-medium">
              <button
                class="flex items-center gap-1 hover:text-slate-700"
                @click="toggleSort('title')"
              >
                Ürün <span :class="sortIcon('title')" class="size-3.5" />
              </button>
            </th>
            <th class="px-3 py-2.5 text-left font-medium">Durum</th>
            <th class="px-3 py-2.5 text-right font-medium">
              <button
                class="flex items-center gap-1 hover:text-slate-700 ml-auto"
                @click="toggleSort('stock')"
              >
                Stok <span :class="sortIcon('stock')" class="size-3.5" />
              </button>
            </th>
            <th class="px-3 py-2.5 text-right font-medium hidden md:table-cell">
              Varyant
            </th>
            <th class="px-3 py-2.5 text-left font-medium hidden lg:table-cell">
              Tip
            </th>
            <th class="px-3 py-2.5 text-left font-medium hidden lg:table-cell">
              Marka
            </th>
            <th class="px-6 py-2.5 text-right font-medium">
              <button
                class="flex items-center gap-1 hover:text-slate-700 ml-auto"
                @click="toggleSort('price')"
              >
                Fiyat <span :class="sortIcon('price')" class="size-3.5" />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="p in paged"
            :key="p.id"
            class="border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
            @click="editProduct(p)"
          >
            <td class="px-6 py-2.5">
              <div class="flex items-center gap-3">
                <div
                  class="size-11 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0 flex items-center justify-center"
                >
                  <img
                    v-if="p.image"
                    :src="p.image"
                    class="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <span v-else class="i-lucide-image size-4 text-slate-300" />
                </div>
                <div class="min-w-0">
                  <span
                    class="font-medium text-slate-800 dark:text-slate-100 line-clamp-2"
                  >
                    {{ p.title }}
                  </span>
                  <span
                    v-if="p.parentCode"
                    class="block text-xs text-slate-400 font-mono"
                  >
                    {{ p.parentCode }}
                  </span>
                </div>
              </div>
            </td>
            <td class="px-3 py-2.5">
              <span
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                :class="
                  p.status === 'active'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                "
              >
                <span
                  class="size-1.5 rounded-full"
                  :class="
                    p.status === 'active' ? 'bg-green-500' : 'bg-slate-400'
                  "
                />
                {{ p.status === 'active' ? 'Aktif' : 'Taslak' }}
              </span>
              <!-- Kaynak + kanal rozetleri: ürün nerede doğdu, nerede yaşıyor -->
              <span
                class="block mt-1 text-[10px] font-semibold"
                :class="
                  p.sourceRef && p.sourceRef.startsWith('ticimax:')
                    ? 'text-slate-400'
                    : 'text-violet-600'
                "
                :title="
                  p.sourceRef && p.sourceRef.startsWith('ticimax:')
                    ? 'Ticimax kataloğundan import edildi'
                    : 'Bu panelde doğdu (Excel/elle)'
                "
              >
                {{
                  p.sourceRef && p.sourceRef.startsWith('ticimax:')
                    ? '📥 Ticimax kökenli'
                    : '🏭 Panelde doğdu'
                }}
                <template v-if="p.ticimaxId"> · 🛒 #{{ p.ticimaxId }}</template>
              </span>
            </td>
            <td
              class="px-3 py-2.5 text-right font-semibold tabular-nums"
              :class="
                p.totalStock > 0
                  ? 'text-slate-700 dark:text-slate-200'
                  : 'text-red-500'
              "
            >
              {{ p.totalStock }}
            </td>
            <td
              class="px-3 py-2.5 text-right text-slate-500 dark:text-slate-400 tabular-nums hidden md:table-cell"
            >
              {{ p.variantCount }}
            </td>
            <td
              class="px-3 py-2.5 text-slate-500 dark:text-slate-400 hidden lg:table-cell"
            >
              {{ p.productType || '—' }}
            </td>
            <td
              class="px-3 py-2.5 text-slate-500 dark:text-slate-400 hidden lg:table-cell"
            >
              {{ p.vendor || '—' }}
            </td>
            <td
              class="px-6 py-2.5 text-right font-medium text-slate-700 dark:text-slate-200 tabular-nums"
            >
              {{ priceLabel(p) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Sayfalama -->
    <div
      v-if="!loading && processed.length"
      class="flex items-center justify-between px-6 py-3 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <span class="text-xs text-slate-400">{{ rangeLabel }} ürün</span>
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800"
          :disabled="page <= 1"
          @click="page -= 1"
        >
          Önceki
        </button>
        <span class="text-xs text-slate-500"
          >{{ page }} / {{ totalPages }}</span
        >
        <button
          class="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800"
          :disabled="page >= totalPages"
          @click="page += 1"
        >
          Sonraki
        </button>
      </div>
    </div>
  </div>
</template>
